import React, { useEffect, useState } from "react";
import {
  Sun, Moon, Cloud, CloudRain, CloudDrizzle, CloudSnow, CloudFog, CloudLightning,
  Wind, Droplets, Sunrise, Sunset, RefreshCw, Thermometer, AlertTriangle,
} from "lucide-react";
import api from "@/lib/api";

function iconFor(condition = "", isDay = true) {
  const c = condition.toLowerCase();
  if (c.includes("thunder")) return CloudLightning;
  if (c.includes("snow")) return CloudSnow;
  if (c.includes("drizzle")) return CloudDrizzle;
  if (c.includes("rain") || c.includes("shower")) return CloudRain;
  if (c.includes("fog")) return CloudFog;
  if (c.includes("cloud") || c.includes("overcast")) return Cloud;
  return isDay ? Sun : Moon;
}

function lastUpdated(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}

// variant: "compact" (public strip), "card" (portal/admin), "display" (welcome screen)
export default function WeatherPanel({ variant = "card", admin = false, className = "" }) {
  const [w, setW] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/weather").then((r) => setW(r.data)).catch(() => setW(false));
  useEffect(() => { load(); const t = setInterval(load, 600000); return () => clearInterval(t); }, []);

  const refresh = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/weather/refresh"); setW(data.weather); }
    catch { load(); } finally { setBusy(false); }
  };

  if (w === null) return null;
  const unavailable = !w || !w.ok;

  if (unavailable) {
    if (variant === "compact") return null;
    return (
      <div className={`glass rounded-xl p-4 text-sm text-muted-foreground flex items-center gap-2 ${className}`} data-testid="weather-unavailable">
        <Cloud size={16} className="text-muted-foreground" /> Weather information is temporarily unavailable.
      </div>
    );
  }

  const Icon = iconFor(w.condition, w.is_day);

  if (variant === "compact") {
    return (
      <div className={`inline-flex items-center gap-3 glass rounded-full pl-3 pr-4 py-2 ${className}`} data-testid="weather-compact">
        <Icon size={20} className="text-gold" />
        <span className="text-sm text-foreground/90">{w.temperature_f}°F · {w.condition}</span>
        <span className="text-xs text-muted-foreground hidden sm:inline">at The D</span>
        {w.is_severe && <AlertTriangle size={14} className="text-amber-300" title={w.severe_note} />}
      </div>
    );
  }

  if (variant === "display") {
    return (
      <div className="flex items-center gap-4 text-white/80" data-testid="weather-display">
        <Icon size={40} className="text-[#B7942E]" />
        <div className="text-left">
          <p className="font-serif text-4xl leading-none">{w.temperature_f}°F</p>
          <p className="text-sm text-white/60 mt-1">{w.condition} · H {w.high_f}° / L {w.low_f}°</p>
        </div>
      </div>
    );
  }

  // card
  return (
    <div className={`glass rounded-xl p-5 ${className}`} data-testid="weather-card">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] tracking-label uppercase text-muted-foreground">Weather at The D</p>
        <div className="flex items-center gap-2">
          {w.stale && <span className="text-[10px] text-amber-300">last known</span>}
          {admin && (
            <button onClick={refresh} disabled={busy} aria-label="Refresh weather" data-testid="weather-refresh"
              className="text-muted-foreground hover:text-gold disabled:opacity-40">
              <RefreshCw size={13} className={busy ? "animate-spin" : ""} />
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4">
        <Icon size={44} className="text-gold shrink-0" />
        <div>
          <p className="font-serif text-4xl leading-none text-foreground">{w.temperature_f}°F</p>
          <p className="text-sm text-muted-foreground mt-1">{w.condition} · Feels {w.feels_like_f}°F</p>
        </div>
        <div className="ml-auto text-right text-sm">
          <p className="text-gold">H {w.high_f}° / L {w.low_f}°</p>
          <p className="text-muted-foreground text-xs mt-1">Rain {w.precipitation_probability ?? 0}%</p>
        </div>
      </div>

      {w.is_severe && (
        <div className="mt-3 flex items-start gap-2 text-xs text-amber-300 bg-amber-400/5 border border-amber-400/20 rounded-lg px-3 py-2" data-testid="weather-severe">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" /> {w.severe_note}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-2 gap-x-3 mt-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><Droplets size={13} className="text-pool" /> {w.humidity}% humidity</span>
        <span className="flex items-center gap-1.5"><Wind size={13} className="text-pool" /> {w.wind_mph} mph</span>
        <span className="flex items-center gap-1.5"><Sunrise size={13} className="text-gold" /> {w.sunrise}</span>
        <span className="flex items-center gap-1.5"><Sunset size={13} className="text-gold" /> {w.sunset}</span>
      </div>

      {w.forecast?.length > 1 && (
        <div className="flex justify-between mt-4 pt-3 border-t border-white/5" data-testid="weather-forecast">
          {w.forecast.slice(1, 4).map((f) => {
            const FI = iconFor(f.condition, true);
            return (
              <div key={f.date} className="text-center flex-1">
                <p className="text-[10px] uppercase tracking-label text-muted-foreground">{f.day}</p>
                <FI size={18} className="text-gold mx-auto my-1.5" />
                <p className="text-xs">{f.high_f}° <span className="text-muted-foreground">{f.low_f}°</span></p>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[10px] text-muted-foreground/70 mt-3 flex items-center gap-1">
        <Thermometer size={11} /> Updated {lastUpdated(w.updated_at)} · {w.location} · informational only
      </p>
    </div>
  );
}
